import {
    flash,
    FormGroup,
    getSymbol,
    GooglePlacesAutocomplete,
    Label,
    Modal,
    SubmitButton,
    Switch,
} from "@xola/ui-kit";
import clsx from "clsx";
import { DurationUnit, FormField, InputField, SecondaryButton, SelectField, TextareaField } from "common";
import { isNumber } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { AmountType, CatalogItemObject, GooglePlaceLocation, PickupCatalogItem, SelectionState, UnitType } from "types";
import { object, string } from "yup";
import { api } from "@/api/api";
import { TimePickerPopover } from "@/components/Common/TimePickerPopover";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { CurrencyInputField } from "@/components/Field/CurrencyInputField";
import { config } from "@/config";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface EditGlobalPickupLocationModalProps {
    pickupLocation?: PickupCatalogItem;
    isOpen: boolean;
    onClose: () => void;
}

interface PickupLocationFormValues {
    name: string;
    caption?: string;
    location: GooglePlaceLocation;
    pricePerPerson: {
        price: number;
        priceType: AmountType;
    };
    pricePerGroup: {
        price: number;
        priceType: AmountType;
    };
    arrivalOffset: number | null;
    products: SelectionState;
}

export const CreatePickupLocationModal = ({ pickupLocation, isOpen, onClose }: EditGlobalPickupLocationModalProps) => {
    return (
        <Modal size="medium" isOpen={isOpen} onClose={onClose}>
            <ModalContent pickupLocation={pickupLocation} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ pickupLocation, onClose }: Except<EditGlobalPickupLocationModalProps, "isOpen">) => {
    const seller = getSeller();

    const currency = seller?.currency;

    const { mutateAsync: upsertGlobalPickupLocation, isLoading } = useMutation(
        async (payload: Omit<PickupLocationFormValues, "products">) => {
            if (pickupLocation?.id) {
                return api.put<PickupCatalogItem>(`/catalogItems/${pickupLocation.id}`, payload);
            }

            return api.post<PickupCatalogItem>("/catalogItems", payload);
        },
    );

    const handleSubmit = async (values: any) => {
        const postalAddress = values.location?.postal_address ?? values.location?.postalAddress ?? {};
        const formattedAddress = values.location?.formatted_address ?? values.location?.formattedAddress;
        const fullAddress = [values.location.name ?? "", ...(postalAddress.addressLines ?? [])]
            .filter(Boolean)
            .join(", ");
        const payload = {
            name: values.name,
            caption: values.caption,
            object: CatalogItemObject.PICKUP_CATALOG_ITEM,
            location: {
                name: values.location.name,
                formattedAddress,
                postalAddress: {
                    streetLines: fullAddress,
                    postalCode: postalAddress.postalCode,
                    region: postalAddress.administrativeArea,
                    countryCode: postalAddress.regionCode,
                    city: postalAddress.locality,
                },
                externalIdentifiers: [
                    {
                        remoteId: values.location.place_id ?? values.location.externalIdentifiers?.[0]?.remoteId,
                        remoteSystem: "google_places",
                    },
                ],
            },
            arrivalOffset: isNumber(values?.arrivalOffset) ? values.arrivalOffset : 0,
            pricePerPerson: values.pricePerPerson,
            pricePerGroup: values.pricePerGroup,
            seller: { id: getSeller().id },
            type: UnitType.PICKUP,
            unitType: UnitType.PICKUP,
        };

        const { data } = await upsertGlobalPickupLocation(payload);

        updateCache("catalogItems", data);
        flash.success("Pickup Location saved");

        onClose();
    };

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={getValidationSchema()}
            initialValues={getInitialValues(pickupLocation)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, setFieldValue, values, errors, dirty, isSubmitting }) => {
                return (
                    <>
                        <Modal.Header data-testid="pickup-location-modal-header">
                            {pickupLocation ? "Edit" : "New"} Pickup Location
                        </Modal.Header>
                        <Modal.Body>
                            <FormField name="name" label="Name">
                                <InputField isRequired data-testid="pickup-location-name-input" />
                            </FormField>

                            <FormField name="location" label="Google Place">
                                <GooglePlacesAutocomplete
                                    apiBaseUrl={config.google.place_url}
                                    remoteId={values.location?.externalIdentifiers?.[0]?.remoteId}
                                    initialValue={
                                        values.location?.name ??
                                        (values.location?.postalAddress?.streetLines
                                            ? values.location.postalAddress.streetLines.split(",")[0].trim()
                                            : "")
                                    }
                                    onSelect={(place: GooglePlaceLocation) => {
                                        setFieldValue("location", place);
                                        setFieldValue("name", place.name);
                                    }}
                                />
                                {values.location ? (
                                    <div className="p-2 text-gray-darker">
                                        {getFormattedAddress(values.location as RawGooglePlaceLocation)}
                                    </div>
                                ) : null}
                            </FormField>
                            <div className="flex min-w-[240px]">
                                <FormField
                                    name="pricePerPerson.price"
                                    label="Price Per Guest"
                                    tooltip="This price applies when each guest selects their own pickup location"
                                    className="w-[100px]"
                                >
                                    {values.pricePerPerson.priceType === AmountType.PERCENT ? (
                                        <InputField
                                            min={0}
                                            type="number"
                                            data-testid="pickup-location-price-per-person-input"
                                            className="block w-full rounded-r-none border-r-0"
                                        />
                                    ) : (
                                        <CurrencyInputField
                                            min={0}
                                            data-testid="pickup-location-price-per-person-input"
                                            className="block w-full rounded-r-none border-r-0"
                                        />
                                    )}
                                </FormField>

                                <FormField name="pricePerPerson.priceType" label="" className="mt-auto w-[60px]">
                                    <SelectField
                                        data-testid="pickup-location-price-per-person-type-select"
                                        className="h-10 rounded-l-none"
                                    >
                                        <option className="no-translate" value={AmountType.ABSOLUTE}>
                                            {getSymbol(currency)}
                                        </option>
                                        <option className="no-translate" value={AmountType.PERCENT}>
                                            %
                                        </option>
                                    </SelectField>
                                </FormField>
                                <span
                                    data-testid="pickup-location-price-per-person-unit-label"
                                    className="m-auto ml-2 whitespace-nowrap"
                                >
                                    {values.pricePerPerson.priceType === AmountType.ABSOLUTE
                                        ? "per guest"
                                        : "of guest price"}
                                </span>
                            </div>

                            <div className="flex min-w-[240px]">
                                <FormField
                                    name="pricePerGroup.price"
                                    label="Price Per Group"
                                    tooltip="This price applies when the organizer selects one pickup location for the entire group"
                                    className="w-[100px]"
                                >
                                    {values.pricePerGroup.priceType === AmountType.PERCENT ? (
                                        <InputField
                                            min={0}
                                            type="number"
                                            data-testid="pickup-location-price-per-group-input"
                                            className="block w-full rounded-r-none border-r-0"
                                        />
                                    ) : (
                                        <CurrencyInputField
                                            min={0}
                                            data-testid="pickup-location-price-per-group-input"
                                            className="block w-full rounded-r-none border-r-0"
                                        />
                                    )}
                                </FormField>
                                <FormField name="pricePerGroup.priceType" label="" className="mt-auto w-[60px]">
                                    <SelectField
                                        data-testid="pickup-location-price-per-group-type-select"
                                        className="h-10 rounded-l-none"
                                    >
                                        <option className="no-translate" value={AmountType.ABSOLUTE}>
                                            {getSymbol(currency)}
                                        </option>
                                        <option className="no-translate" value={AmountType.PERCENT}>
                                            %
                                        </option>
                                    </SelectField>
                                </FormField>
                                <span
                                    data-testid="pickup-location-price-per-group-unit-label"
                                    className="m-auto ml-2 whitespace-nowrap"
                                >
                                    {values.pricePerGroup.priceType === AmountType.ABSOLUTE
                                        ? "per group"
                                        : "of reservation total"}
                                </span>
                            </div>

                            <FormField name="caption" label="Instructions">
                                <TextareaField rows={6} data-testid="pickup-location-instructions-textarea" />
                            </FormField>

                            <FormGroup>
                                <Label>Pickup Time</Label>
                                <Switch.Group className="flex ">
                                    <Switch
                                        isChecked={isNumber(values?.arrivalOffset) ?? false}
                                        size="medium"
                                        data-testid="pickup-location-pickup-time-switch"
                                        onChange={(value: boolean) => setFieldValue("arrivalOffset", value ? 0 : null)}
                                    />
                                    {isNumber(values?.arrivalOffset) ? (
                                        <div className="ml-2">
                                            Guest must arrive at pickup location
                                            <TimePickerPopover
                                                value={values?.arrivalOffset ?? 0}
                                                preferredUnit={DurationUnit.MINUTE}
                                                isError={Boolean(errors?.arrivalOffset)}
                                                dataTestId="pickup-location-time-popover-trigger"
                                                classNames={{
                                                    text: clsx(
                                                        errors?.arrivalOffset ? "!text-danger" : "!text-gray-darker",
                                                        "mx-1",
                                                    ),
                                                }}
                                                onApply={(value) => {
                                                    setFieldValue("arrivalOffset", value);
                                                }}
                                            />
                                            before event start time
                                        </div>
                                    ) : (
                                        <p className="pl-2">Enable Pickup Time</p>
                                    )}
                                </Switch.Group>
                            </FormGroup>
                        </Modal.Body>
                        <Modal.Footer>
                            <SecondaryButton
                                data-testid="pickup-location-modal-cancel-button"
                                onClick={() => {
                                    onClose();
                                }}
                            >
                                Cancel
                            </SecondaryButton>

                            <SubmitButton
                                isLoading={isLoading || isSubmitting}
                                disabled={!dirty}
                                data-testid="pickup-location-modal-save-button"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

const getInitialValues = (pickupLocation?: PickupCatalogItem) => {
    return {
        name: pickupLocation?.name ?? "",
        caption: pickupLocation?.caption ?? "",
        location: pickupLocation?.location ?? null,
        pricePerPerson: {
            price: pickupLocation?.prices?.perPerson?.price ?? "",
            priceType: pickupLocation?.prices?.perPerson?.priceType ?? AmountType.ABSOLUTE,
        },
        pricePerGroup: {
            price: pickupLocation?.prices?.perGroup?.price ?? "",
            priceType: pickupLocation?.prices?.perGroup?.priceType ?? AmountType.ABSOLUTE,
        },
        arrivalOffset:
            isNumber(pickupLocation?.arrivalOffset) && pickupLocation!.arrivalOffset > 0
                ? pickupLocation!.arrivalOffset
                : null,
    };
};

const getValidationSchema = () => {
    return object({
        name: string().required("is required"),
        location: object().typeError("Location is required").required("is required"),
    });
};

interface RawGooglePlaceLocation extends GooglePlaceLocation {
    formatted_address?: string;
}

const getFormattedAddress = (location: RawGooglePlaceLocation) => {
    return location.formattedAddress ?? location.formatted_address ?? "";
};
